import type { ReactNode } from 'react'

export interface SegmentedOption<T extends string> {
  value: T
  label: string
  icon: ReactNode
}

interface SegmentedSwitchProps<T extends string> {
  options: [SegmentedOption<T>, SegmentedOption<T>]
  value: T
  onChange: (value: T) => void
  'aria-label'?: string
}

/**
 * Two-segment switch rendered as a single clickable control. The active segment
 * shows icon + label; the inactive one collapses to an icon-only pill. Clicking
 * anywhere selects the other segment.
 */
export function SegmentedSwitch<T extends string>({
  options,
  value,
  onChange,
  'aria-label': ariaLabel
}: SegmentedSwitchProps<T>) {
  const next = options.find((o) => o.value !== value) ?? options[0]
  return (
    <button
      type='button'
      className='ramp-switch'
      onClick={() => onChange(next.value)}
      aria-label={ariaLabel ?? `Switch to ${next.label}`}
      title={ariaLabel ?? `Switch to ${next.label}`}
    >
      {options.map((option) => {
        const isActive = option.value === value
        return (
          <span key={option.value} className={`ramp-switch-option${isActive ? ' is-active' : ''}`}>
            {option.icon}
            <span className='ramp-switch-label'>{option.label}</span>
          </span>
        )
      })}
    </button>
  )
}
